import React, { useState, useMemo } from 'react';
import {
    ChevronLeft, ChevronRight, Plus, Clock,
    Flag, MoreHorizontal, Calendar as CalendarIcon,
    Grid3X3, List, ChevronDown
} from 'lucide-react';
import { Issue, User, Project } from '../../types';

interface BordioCalendarProps {
    issues: Issue[];
    users: User[];
    projects: Project[];
    onCreateIssue: () => void;
    onIssueClick: (issue: Issue) => void;
}

type CalendarView = 'month' | 'week';

export const BordioCalendar: React.FC<BordioCalendarProps> = ({
    issues,
    users,
    projects,
    onCreateIssue,
    onIssueClick,
}) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [view, setView] = useState<CalendarView>('month');

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    // Get calendar days for month view
    const calendarDays = useMemo(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        // Adjust for Monday start
        let startDay = firstDay.getDay() - 1;
        if (startDay < 0) startDay = 6;

        const days: Date[] = [];

        // Previous month days
        for (let i = startDay - 1; i >= 0; i--) {
            const date = new Date(year, month, -i);
            days.push(date);
        }

        // Current month days
        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push(new Date(year, month, i));
        }

        // Next month days to fill grid
        const remaining = 42 - days.length;
        for (let i = 1; i <= remaining; i++) {
            days.push(new Date(year, month + 1, i));
        }

        return days;
    }, [currentDate]);

    // Get week days
    const weekDays = useMemo(() => {
        const start = new Date(currentDate);
        const dayOfWeek = start.getDay();
        const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        start.setDate(start.getDate() + diff);

        return Array.from({ length: 7 }, (_, i) => {
            const date = new Date(start);
            date.setDate(start.getDate() + i);
            return date;
        });
    }, [currentDate]);

    const getIssuesForDate = (date: Date) => {
        const dateStr = date.toISOString().split('T')[0];
        return issues.filter(issue => {
            if (issue.dueDate) {
                return issue.dueDate.split('T')[0] === dateStr;
            }
            return false;
        });
    };

    const isToday = (date: Date) => {
        return date.toDateString() === today.toDateString();
    };

    const isCurrentMonth = (date: Date) => {
        return date.getMonth() === currentDate.getMonth();
    };

    const navigatePrev = () => {
        const newDate = new Date(currentDate);
        if (view === 'month') {
            newDate.setMonth(newDate.getMonth() - 1);
        } else {
            newDate.setDate(newDate.getDate() - 7);
        }
        setCurrentDate(newDate);
    };

    const navigateNext = () => {
        const newDate = new Date(currentDate);
        if (view === 'month') {
            newDate.setMonth(newDate.getMonth() + 1);
        } else {
            newDate.setDate(newDate.getDate() + 7);
        }
        setCurrentDate(newDate);
    };

    const goToToday = () => {
        setCurrentDate(new Date());
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'done': return 'bg-accent-green';
            case 'in-progress': return 'bg-accent-orange';
            case 'in-review': return 'bg-accent-purple';
            case 'to-do': return 'bg-brand-400';
            default: return 'bg-surface-400';
        }
    };

    const getStatusBorderColor = (status: string) => {
        switch (status) {
            case 'done': return 'border-l-green-400';
            case 'in-progress': return 'border-l-orange-400';
            case 'in-review': return 'border-l-purple-400';
            case 'to-do': return 'border-l-blue-400';
            default: return 'border-l-surface-300';
        }
    };

    const hours = Array.from({ length: 13 }, (_, i) => i + 7); // 7 AM to 7 PM

    return (
        <div className="flex-1 flex flex-col overflow-hidden bg-white">
            {/* Calendar Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-surface-100">
                <div className="flex items-center gap-4">
                    <h2 className="text-lg font-semibold text-surface-900">
                        {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                    </h2>
                    <div className="flex items-center gap-1">
                        <button onClick={navigatePrev} className="btn-ghost p-1.5">
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button onClick={goToToday} className="btn-ghost text-xs px-3">
                            Today
                        </button>
                        <button onClick={navigateNext} className="btn-ghost p-1.5">
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <div className="flex items-center bg-surface-100 rounded-lg p-0.5">
                        <button
                            onClick={() => setView('month')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${view === 'month' ? 'bg-white text-surface-900 shadow-sm' : 'text-surface-500 hover:text-surface-700'
                                }`}
                        >
                            <Grid3X3 className="w-3.5 h-3.5 inline mr-1" />
                            Month
                        </button>
                        <button
                            onClick={() => setView('week')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${view === 'week' ? 'bg-white text-surface-900 shadow-sm' : 'text-surface-500 hover:text-surface-700'
                                }`}
                        >
                            <List className="w-3.5 h-3.5 inline mr-1" />
                            Week
                        </button>
                    </div>
                    <button onClick={onCreateIssue} className="btn-primary py-1.5 text-sm">
                        <Plus className="w-4 h-4" />
                        Add Task
                    </button>
                </div>
            </div>

            {/* Calendar Content */}
            {view === 'month' ? (
                <div className="flex-1 overflow-auto">
                    {/* Day Headers */}
                    <div className="grid grid-cols-7 border-b border-surface-100">
                        {dayNames.map(day => (
                            <div key={day} className="px-3 py-2 text-xs font-semibold text-surface-500 text-center uppercase tracking-wider">
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 flex-1">
                        {calendarDays.map((date, index) => {
                            const dayIssues = getIssuesForDate(date);
                            const isTodayDate = isToday(date);
                            const isInMonth = isCurrentMonth(date);

                            return (
                                <div
                                    key={index}
                                    className={`min-h-[120px] border-b border-r border-surface-100 p-2 transition-colors ${isTodayDate ? 'bg-brand-50/30' : ''
                                        } ${!isInMonth ? 'bg-surface-50/50' : ''} hover:bg-surface-50/70`}
                                >
                                    <div className="flex items-center justify-between mb-1">
                                        <span className={`text-xs font-medium ${isTodayDate
                                                ? 'bg-brand-600 text-white w-6 h-6 rounded-full flex items-center justify-center'
                                                : isInMonth ? 'text-surface-900' : 'text-surface-400'
                                            }`}>
                                            {date.getDate()}
                                        </span>
                                        {dayIssues.length > 0 && (
                                            <span className="text-2xs text-surface-400">{dayIssues.length}</span>
                                        )}
                                    </div>

                                    <div className="space-y-1">
                                        {dayIssues.slice(0, 3).map(issue => (
                                            <button
                                                key={issue.id}
                                                onClick={() => onIssueClick(issue)}
                                                className={`w-full text-left px-2 py-1 rounded text-2xs font-medium truncate border-l-2 ${getStatusBorderColor(issue.status)} bg-white hover:bg-surface-50 transition-colors`}
                                            >
                                                {issue.summary}
                                            </button>
                                        ))}
                                        {dayIssues.length > 3 && (
                                            <button className="text-2xs text-brand-600 hover:text-brand-700 font-medium px-2">
                                                +{dayIssues.length - 3} more
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : (
                /* Week View */
                <div className="flex-1 overflow-auto">
                    <div className="flex">
                        {/* Time column */}
                        <div className="w-16 flex-shrink-0 border-r border-surface-100">
                            <div className="h-12 border-b border-surface-100" /> {/* Header spacer */}
                            {hours.map(hour => (
                                <div key={hour} className="h-16 border-b border-surface-50 px-2 py-1">
                                    <span className="text-2xs text-surface-400">
                                        {hour > 12 ? `${hour - 12} PM` : hour === 12 ? '12 PM' : `${hour} AM`}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Day columns */}
                        <div className="flex-1 grid grid-cols-7">
                            {weekDays.map((date, index) => {
                                const dayIssues = getIssuesForDate(date);
                                const isTodayDate = isToday(date);

                                return (
                                    <div key={index} className="border-r border-surface-100">
                                        {/* Day header */}
                                        <div className={`h-12 flex flex-col items-center justify-center border-b border-surface-100 ${isTodayDate ? 'bg-brand-50/50' : ''
                                            }`}>
                                            <span className="text-2xs text-surface-500 uppercase">{dayNames[index]}</span>
                                            <span className={`text-sm font-semibold ${isTodayDate
                                                    ? 'bg-brand-600 text-white w-7 h-7 rounded-full flex items-center justify-center'
                                                    : 'text-surface-900'
                                                }`}>
                                                {date.getDate()}
                                            </span>
                                        </div>

                                        {/* Time slots */}
                                        <div className="relative">
                                            {hours.map(hour => (
                                                <div key={hour} className={`h-16 border-b border-surface-50 ${isTodayDate ? 'bg-brand-50/10' : ''
                                                    }`} />
                                            ))}

                                            {/* Issues overlay */}
                                            <div className="absolute top-0 left-0 right-0 p-1 space-y-1">
                                                {dayIssues.map(issue => (
                                                    <button
                                                        key={issue.id}
                                                        onClick={() => onIssueClick(issue)}
                                                        className={`w-full text-left p-1.5 rounded text-2xs font-medium border-l-2 ${getStatusBorderColor(issue.status)} bg-white shadow-soft hover:shadow-card transition-shadow`}
                                                    >
                                                        <p className="truncate">{issue.summary}</p>
                                                        <span className="text-surface-400">{issue.key}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
